import React from 'react';
import { Grid, InputAdornment } from '@mui/material';
import { FormInputDropdown } from '../../../../PersonalInfoTab/Form/Controls/FormInputDropdown';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import FormInputTextField from '@AMIEWEB/Candidate/CandidateProfile/WorkExperience/Edit/components/FormInputTextField';
import { useAddEditReferenceStyle } from './AddEditReference.style';
import { FormInputDatePicker } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDatePicker';

const ReferenceDetails = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation();
  const { classes } = useAddEditReferenceStyle();
  const {
    watch,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Grid container columnSpacing={6} rowSpacing={4} px={4} pt={4} pb={2}>
      <Grid item xs={4}>
        <FormInputTextField
          name="positionHeld"
          label={t('candidate.workExperience.addEditReference.referenceDetails.positionHeld')}
          required={false}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={4} display={'flex'}>
        <Grid item xs={6}>
          <FormInputTextField
            name="unitName"
            label={t('candidate.workExperience.addEditReference.referenceDetails.unit')}
            required={false}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormInputTextField
            name="unitSpecialty"
            label={t('candidate.workExperience.addEditReference.referenceDetails.unitSpecialty')}
            required={false}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <Grid item xs={9}>
          <FormInputDropdown
            name="status"
            label={t('candidate.workExperience.addEditReference.referenceDetails.status')}
            require={false}
            disabled={disabled}
            readOnly={disabled}
            options={[]}
            handleChange={event => setValue('status', event.target.value)}
            message={errors['status']?.message || ''}
            standard
            showErrorAttribute={true}
            useMenuItem
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <FormInputDropdown
          name="shiftWorked"
          label={t('candidate.workExperience.addEditReference.referenceDetails.shiftWorked')}
          require={false}
          disabled={disabled}
          readOnly={disabled}
          options={[]}
          handleChange={event => setValue('shiftWorked', event.target.value)}
          message={errors['shiftWorked']?.message || ''}
          standard
          showErrorAttribute={true}
          useMenuItem
        />
      </Grid>
      <Grid item xs={4} display={'flex'}>
        <Grid item xs={6}>
          <FormInputTextField
            name="numberOfClinicians"
            label={t('candidate.workExperience.addEditReference.referenceDetails.numberOfClinicians')}
            required={false}
            type={'number'}
            inputProps={{ min: 0, max: 9 }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormInputTextField
            name="numberOfPatients"
            label={t('candidate.workExperience.addEditReference.referenceDetails.numberOfPatients')}
            required={false}
            disabled={disabled}
            maxLength={10}
            maxLengthErrorMessage={t(
              'candidate.workExperience.addEditReference.referenceContacts.textFieldErrorMessage',
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={4} display={'flex'}>
        <Grid item xs={6}>
          <FormInputDropdown
            name="referenceType"
            label={t('candidate.workExperience.addEditReference.referenceDetails.referenceType')}
            require={false}
            disabled={disabled}
            readOnly={disabled}
            options={[]}
            handleChange={event => setValue('referenceType', event.target.value)}
            message={errors['referenceType']?.message || ''}
            standard
            showErrorAttribute={true}
            useMenuItem
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormInputDatePicker
            name={'referenceDate'}
            label={t('candidate.workExperience.addEditReference.referenceDetails.referenceDate')}
            readOnly={disabled}
            customClass={classes.datePickerInput}
            labelClass={classes.label}
            trailingIconVisible={true}
            inputValue={watch('referenceDate')}
          ></FormInputDatePicker>
        </Grid>
      </Grid>
      <Grid item xs={4} display={'flex'} flexDirection={'column'} mt={2}>
        <Grid item xs={12} ml={2}>
          <Controller
            name={'chargeExperience'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('candidate.workExperience.addEditReference.referenceDetails.chargeExperience')}
                checkboxName={'EditReferenceSupervisorExperience'}
                id={'EditReferenceSupervisorExperience'}
                isdisabled={disabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} ml={2} mt={-3}>
          <Controller
            name={'eligibleForRehire'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value === 'Y' || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('candidate.workExperience.addEditReference.referenceDetails.eligibleForRehire')}
                checkboxName={'EditReferenceEligibleForRehire'}
                id={'EditReferenceEligibleForRehire'}
                isdisabled={disabled}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={4} display={'flex'}>
        <Grid item xs={6}>
          <FormInputTextField
            name="numberOfBeds"
            label={t('candidate.workExperience.addEditReference.referenceDetails.numberOfBeds')}
            required={false}
            type={'number'}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormInputTextField
            name="productivityPercentage"
            label={t('candidate.workExperience.addEditReference.referenceDetails.productivityPercentage')}
            required={false}
            disabled={disabled}
            type={'number'}
            InputProps={{
              inputProps: { min: 0, max: 100 },
              startAdornment: <InputAdornment position="start">{'%'}</InputAdornment>,
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <FormInputTextField
          name="comment"
          label={t('candidate.workExperience.addEditReference.referenceDetails.comment')}
          required={false}
          multiline={true}
          disabled={disabled}
          InputProps={{
            className: classes.textAreaInputStyle,
            inputProps: { maxLength: 1000 },
          }}
          showHelperText={true}
          characterLimit={1000}
        />
      </Grid>
      <Grid item xs={4} mt={getValues('comment')?.length === undefined ? 0 : -3.5}>
        <FormInputTextField
          name="explanation"
          label={t('candidate.workExperience.addEditReference.referenceDetails.explanation')}
          required={false}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={4} mt={getValues('comment')?.length === undefined ? -11.5 : -15}>
        <FormInputTextField
          name="unitDescription"
          label={t('candidate.workExperience.addEditReference.referenceDetails.unitDescription')}
          required={false}
          multiline={true}
          disabled={disabled}
          InputProps={{
            className: classes.textAreaInputStyle,
            inputProps: { maxLength: 1000 },
          }}
          showHelperText={true}
          characterLimit={1000}
        />
      </Grid>
      <Grid item xs={4} display={'flex'} flexDirection={'column'} mt={-2}>
        <Grid item xs={12} ml={2}>
          <Controller
            name={'mayShareWithTraveler'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('candidate.workExperience.addEditReference.referenceDetails.okToShareWithCandidate')}
                checkboxName={'EditReferenceMayShareWithTraveler'}
                id={'EditReferenceMayShareWithTraveler'}
                isdisabled={disabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} ml={2} mt={3}>
          <Controller
            name={'sharedWithTraveler'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t(
                  'candidate.workExperience.addEditReference.referenceDetails.haveYouSharedWithCandidate',
                )}
                checkboxName={'EditReferenceSharedWithTraveler'}
                id={'EditReferenceSharedWithTraveler'}
                isdisabled={disabled}
              />
            )}
          />
        </Grid>
      </Grid>{' '}
    </Grid>
  );
};

export default React.memo(ReferenceDetails);
